// packages/shared/src/schemas/toggleable-role.ts
// CRUD validators for the /api/toggleable-roles route (wired in plan 07-04).
// Backs the /role-toggle utility command (CMD-07, plan 07-03).
//
// Mirrors the Phase 3 pattern: hoisted into @guardian/shared so the SPA and
// guardian-worker both validate the same wire shape.

import { z } from 'zod';

// Discord snowflake = 17–20 digit integer string.
const ROLE_ID_RE = /^\d{17,20}$/;

/**
 * POST /api/toggleable-roles — create a new toggleable-role row for the BASTN guild.
 * The handler enforces the (guild_id, role_id) composite PK; duplicate inserts
 * surface as 409 NAME_TAKEN-style errors.
 */
export const createToggleableRoleSchema = z.object({
  roleId:   z.string().regex(ROLE_ID_RE, 'Discord role ID must be a 17-20 digit snowflake'),
  label:    z.string().min(1, 'Label is required').max(80, 'Label max 80 chars'),
  position: z.number().int().nonnegative().optional(),
});

/**
 * PATCH /api/toggleable-roles/:roleId — update label and/or position. Cannot
 * change the role_id (PK component) — operators must DELETE + POST to remap.
 */
export const updateToggleableRoleSchema = z
  .object({
    label:    z.string().min(1, 'Label is required').max(80, 'Label max 80 chars').optional(),
    position: z.number().int().nonnegative().optional(),
  })
  .refine((v) => v.label !== undefined || v.position !== undefined, {
    message: 'At least one of label or position is required',
  });

export type CreateToggleableRoleBody = z.infer<typeof createToggleableRoleSchema>;
export type UpdateToggleableRoleBody = z.infer<typeof updateToggleableRoleSchema>;
